import { EventType } from "@/analytics/analytics-events";
import type {
  ChangeFeatureTourPageEventProperties,
  FeatureTourEventProperties,
} from "@/analytics/feature-tour-events-properties";
import type { ViewerTool } from "@/store/features/features";
import {
  selectEnabledFeatures,
  type EnabledFeature,
} from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { FeatureTour } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useEffect, useMemo, useState, type JSX } from "react";
import { featureTourTopics, type FeatureTourTopic } from "./feature-tour-data";
import type { FeatureTourViewStatus } from "./fue-flags";
import { useFeatureTours } from "./use-feature-tours";

type FeatureTourManagerProps = {
  /** The tool for which to show feature tours. */
  tool: ViewerTool;
};

/** @returns manager deciding which feature tours to show to the user. */
export function FeatureTourManager({
  tool,
}: FeatureTourManagerProps): JSX.Element | null {
  const enabledFeatures = useAppSelector(selectEnabledFeatures);
  const {
    areVisibilityStatusesLoaded,
    visibilityStatuses: statuses,
    markStatus,
  } = useFeatureTours();

  // Only show one feature tour per session, even if the user closes it without a decision
  const [isHiddenForSession, setIsHiddenForSession] = useState(false);

  const topic = useMemo(() => {
    if (!areVisibilityStatusesLoaded) return;

    return featureTourTopics.find(
      (topic) =>
        topic.tools.includes(tool) &&
        isUnseen(statuses[topic.topicId]) &&
        hasRequiredFeatures(topic, enabledFeatures),
    );
  }, [areVisibilityStatusesLoaded, enabledFeatures, statuses, tool]);

  const shownTopicId = isHiddenForSession ? undefined : topic?.topicId;

  useEffect(() => {
    if (!shownTopicId) return;
    Analytics.track<FeatureTourEventProperties>(EventType.showFeatureTour, {
      topicId: shownTopicId,
    });
  }, [shownTopicId]);

  if (!topic || isHiddenForSession) return null;

  return (
    <FeatureTour
      pages={topic.pages}
      onNextPage={(pageIndex) =>
        trackPageChange(topic.topicId, "next", pageIndex)
      }
      onPreviousPage={(pageIndex) =>
        trackPageChange(topic.topicId, "previous", pageIndex)
      }
      onJumpToPage={(pageIndex) =>
        trackPageChange(topic.topicId, "page indicator", pageIndex)
      }
      onSkipped={() => {
        Analytics.track<FeatureTourEventProperties>(EventType.skipFeatureTour, {
          topicId: topic.topicId,
        });
        markStatus(topic.topicId, "skipped");
      }}
      onFinished={() => {
        Analytics.track<FeatureTourEventProperties>(
          EventType.completeFeatureTour,
          { topicId: topic.topicId },
        );
        markStatus(topic.topicId, "completed");
      }}
      onClose={() => setIsHiddenForSession(true)}
    />
  );
}

/**
 * Tracks that the user navigated to another page of a feature tour.
 *
 * @param topicId The topic the user is currently viewing
 * @param via How the user navigated to the new page
 * @param pageIndex The index of the page the user navigated to
 */
function trackPageChange(
  topicId: FeatureTourTopic["topicId"],
  via: ChangeFeatureTourPageEventProperties["via"],
  pageIndex: number,
): void {
  Analytics.track<ChangeFeatureTourPageEventProperties>(
    EventType.changeFeatureTourPage,
    { topicId, via, pageIndex },
  );
}

/**
 * @param status The status of a feature tour topic for the current user
 * @returns `true` if the user has not made a decision about the topic yet
 */
function isUnseen(status: FeatureTourViewStatus | undefined): boolean {
  return status !== "completed" && status !== "skipped";
}

/**
 * @param topic The feature tour topic to check
 * @param enabledFeatures The features enabled for the current user
 * @returns `true` if all feature flags required by the topic are enabled
 */
function hasRequiredFeatures(
  topic: FeatureTourTopic,
  enabledFeatures: EnabledFeature[],
): boolean {
  return (
    topic.featureFlags?.every((feature) =>
      enabledFeatures.some(
        (enabledFeature) => enabledFeature.featureFlag === feature,
      ),
    ) ?? true
  );
}
